import { type ReactNode, ChangeEvent, FormEvent } from 'react';
import { IconArrow } from '@/assets/icons/IconArrow';
import { VALIDITY } from '@/constants/validity';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Input } from '@/ui-components/atoms/Inputs/Input/Input';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import {
    AppDownloadButtons,
    DownloadButtonProps,
} from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import styles from './snapshotpopover.module.scss';
import { buildCareRouteUrl } from './careAccountUrls';

const FORGOT_PASSWORD_URL = buildCareRouteUrl('forgotPassword');

interface SnapshotUserPasswordSignInProps {
    alert?: ReactNode;
    appdownloadItems: DownloadButtonProps[];
    hasError: boolean;
    identifier: string;
    invalidMessage?: ReactNode;
    isSubmitting: boolean;
    onBack: () => void;
    onPasswordChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    password: string;
}

export function SnapshotUserPasswordSignIn({
    alert,
    appdownloadItems,
    hasError,
    identifier,
    invalidMessage,
    isSubmitting,
    onBack,
    onPasswordChange,
    onSubmit,
    password,
}: SnapshotUserPasswordSignInProps): React.JSX.Element {
    return (
        <form className={styles.snapshotUserFlow} onSubmit={onSubmit} noValidate>
            <div className={styles.snapshotSubscreenTopBar}>
                <button aria-label="Go back" className={styles.snapshotBackButton} onClick={onBack} type="button">
                    <IconArrow />
                </button>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotSubscreenMain}>
                <div className={styles.snapshotSubscreenSection}>
                    <div className={styles.snapshotEnterPasswordWrapper}>
                        <Text tag="h3" className={styles.snapshotAnonymousTitle}>
                            Enter your password
                        </Text>
                        <Text tag="p" className={styles.snapshotMethodDescription}>
                            For {identifier}
                        </Text>
                    </div>

                    {alert}

                    {hasError && (
                        <div className={styles.snapshotErrorSummary}>
                            <Text tag="p" className={styles.snapshotErrorSummaryText}>
                                Please fix the error in the form below.
                            </Text>
                        </div>
                    )}

                    <Input
                        ariaLabel="Password"
                        inputWrapperStyles={styles.snapshotInputWrapper}
                        invalid={hasError ? VALIDITY.invalid : VALIDITY.untouched}
                        invalidMessage={invalidMessage}
                        label="Password"
                        name="snapshot-password"
                        noSpacing={true}
                        onChange={onPasswordChange}
                        type="password"
                        value={password}
                    />

                    <Text tag="p" className={`${styles.snapshotCopy} ${styles.snapshotCopyLeft}`}>
                        <Link className={styles.inlineLink} href={FORGOT_PASSWORD_URL}>
                            Forgot your password?
                        </Link>
                    </Text>
                </div>

                <div className={styles.snapshotMethodActions}>
                    <Button
                        ariaLabel="Sign in"
                        disabled={isSubmitting}
                        fitting="fill-container"
                        size="small"
                        type="submit"
                        variant="primary"
                    >
                        {isSubmitting ? 'Signing in...' : 'Sign in'}
                    </Button>
                </div>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotAnonymousFooter}>
                <Text tag="h4" className={styles.snapshotAppHeading}>
                    Get the SiriusXM app
                </Text>
                <AppDownloadButtons
                    items={appdownloadItems}
                    className={styles.snapshotDownloadButtons}
                    appleClassName={styles.snapshotStoreBadge}
                    androidClassName={styles.snapshotStoreBadge}
                />
            </div>
        </form>
    );
}
